import { useCallback, useState } from 'react';
import { Schedule } from '@/features/accounts/types';
import { usePauseSchedule } from './usePauseSchedule';
import { useUpdateSchedulePreferences } from '../../configuration/hooks/useUpdateSchedulePreferences';
import { type SchedulePreferencesPayload } from '@/features/accounts/api';

export interface UseScheduleManagementReturn {
  configSchedule: Schedule | null;
  openConfigModal: (schedule: Schedule) => void;
  closeConfigModal: () => void;
  saveConfiguration: (preferences: SchedulePreferencesPayload) => Promise<void>;
  pauseSchedule: ReturnType<typeof usePauseSchedule>;
  pauseScheduleId: number | null;
  requestPause: (scheduleId: number) => void;
  cancelPause: () => void;
  confirmPause: () => Promise<void>;
  activitiesScheduleId: number | null;
  openActivitiesModal: (schedule: Schedule) => void;
  closeActivitiesModal: () => void;
}

export function useScheduleManagement(): UseScheduleManagementReturn {
  const [configSchedule, setConfigSchedule] = useState<Schedule | null>(null);
  const [pauseScheduleId, setPauseScheduleId] = useState<number | null>(null);
  const [activitiesScheduleId, setActivitiesScheduleId] = useState<number | null>(null);

  const pauseSchedule = usePauseSchedule();
  const updatePreferences = useUpdateSchedulePreferences();

  const openConfigModal = useCallback((schedule: Schedule) => {
    setConfigSchedule(schedule);
  }, []);

  const closeConfigModal = useCallback(() => {
    setConfigSchedule(null);
  }, []);

  const saveConfiguration = useCallback(
    async (preferences: SchedulePreferencesPayload) => {
      if (!configSchedule) return;
      await updatePreferences.mutateAsync({ scheduleId: configSchedule.id, preferences });
      setConfigSchedule(null);
    },
    [configSchedule, updatePreferences]
  );

  const requestPause = useCallback((scheduleId: number) => {
    setPauseScheduleId(scheduleId);
  }, []);

  const cancelPause = useCallback(() => {
    setPauseScheduleId(null);
  }, []);

  const confirmPause = useCallback(async () => {
    if (pauseScheduleId === null) return;
    await pauseSchedule.mutateAsync(pauseScheduleId);
    setPauseScheduleId(null);
  }, [pauseScheduleId, pauseSchedule]);

  const openActivitiesModal = useCallback((schedule: Schedule) => {
    setActivitiesScheduleId(schedule.id);
  }, []);

  const closeActivitiesModal = useCallback(() => {
    setActivitiesScheduleId(null);
  }, []);

  return {
    configSchedule,
    openConfigModal,
    closeConfigModal,
    saveConfiguration,
    pauseSchedule,
    pauseScheduleId,
    requestPause,
    cancelPause,
    confirmPause,
    activitiesScheduleId,
    openActivitiesModal,
    closeActivitiesModal,
  };
}
